'use client'

import { useState } from 'react'

import { SearchIcon } from 'lucide-react'

import type { ProfileSearchRecord } from '@/features/profiles/types/profile-search'
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue'
import { useOptimizedRecords } from '@/shared/hooks/useOptimizedQuery'
import { cn } from '@/shared/lib/utils'
import { inputClassName } from '@/shared/ui/input-styles'

import { ProfileSearchResults } from './ProfileSearchResults'

const DEBOUNCE_MS = 400
const MAX_RESULTS = 5

interface FriendsSearchProps {
  onProfileClick: (record: { uid: string }) => void
  className?: string
}

export function FriendsSearch({ onProfileClick, className }: FriendsSearchProps) {
  const [name, setName] = useState('')
  const debouncedName = useDebouncedValue(name, DEBOUNCE_MS)
  const term = debouncedName.trim()
  const hasTerm = term.length > 0

  const { records, isLoading } = useOptimizedRecords<ProfileSearchRecord>({
    path: '/user/profiles',
    params: {
      q: { name_i_cont: term },
      sort: 'name',
      per_page: MAX_RESULTS
    },
    enabled: hasTerm
  })

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div
        className="flex items-center relative h-[41px] w-full"
        role="search"
        aria-label="Buscar amigos por nome"
      >
        <input
          id="friends-search-name"
          type="search"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Buscar por nome..."
          className={cn(inputClassName, 'pr-10')}
          autoComplete="off"
          aria-label="Buscar por nome"
        />
        <SearchIcon
          className="absolute right-3 size-5 text-gray-400 pointer-events-none"
          aria-hidden="true"
        />
      </div>

      {hasTerm ? (
        <ProfileSearchResults
          records={records}
          isLoading={isLoading}
          onProfileClick={record => onProfileClick(record)}
        />
      ) : (
        <p className="py-6 text-center text-sm text-gray-500">
          Digite um nome para buscar amigos.
        </p>
      )}
    </div>
  )
}
